/**
 * One way out of the app for a file the app generated.
 *
 * In a browser this is an anchor click on a blob: URL, which is what every export here has always
 * done. Inside the native shell that silently does nothing: a web view has no download manager, so
 * the click is accepted and no file ever appears. The bytes are instead written into the app's own
 * storage and handed to the operating system's share sheet, which is how a phone saves a file,
 * mails it, or opens it in a spreadsheet.
 *
 * Detection is through the global the native runtime injects rather than an import, so the browser
 * build neither loads nor bundles the native plugins - they are pulled in only on the one platform
 * that can use them.
 */
const native = () => !!window.Capacitor?.isNativePlatform?.();

// The plugin takes base64 rather than a blob, and FileReader is the only conversion that does not
// hold a second copy of a large export as a JS string.
const encode = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(reader.error || new Error('The file could not be read.'));
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.readAsDataURL(blob);
});

const browserSave = (name, blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const saveFile = async (name, blob) => {
  if (!native()) {
    browserSave(name, blob);
    return;
  }
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share'),
  ]);
  // Cache, not Documents: an export is a thing being sent somewhere, not a document the app keeps,
  // and the operating system is free to reclaim it once the person has dealt with it.
  const { uri } = await Filesystem.writeFile({
    path: name,
    data: await encode(blob),
    directory: Directory.Cache,
  });
  await Share.share({ title: name, url: uri });
};

export default saveFile;
